import { Application, Assets, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { destroyPixiApp } from '../../core/destroyPixiApp';
import type { ThemeAssetEntry } from '../../theme';
import { loadUiFonts } from '../board/ui';
import { SlotUnlockBoard, type SlotUnlockPress, type SlotUnlockUi, type SlotUnlockView } from './SlotUnlockBoard';

export interface SlotUnlockArtUrls {
  /** The board's floor (the first slot's reel column). */
  floor: string;
  gem: string;
  lock: string;
  check: string;
  /** The painted gauge: its brass and walnut track, and its gem fill. */
  track: string;
  fill: string;
}

export interface MountSlotUnlockOptions {
  art: SlotUnlockArtUrls;
  /** The painted button plates. */
  plates?: Record<string, ThemeAssetEntry>;
  view: SlotUnlockView;
  /** The slot's picture (its map tile), shown once it loads. */
  pictureUrl?: string | null;
  onPress: (press: SlotUnlockPress) => void;
}

export interface SlotUnlockHandle {
  show: (view: SlotUnlockView) => void;
  destroy: () => void;
}

declare global {
  interface Window {
    /** The slot unlock board on screen, for browser tests. */
    __slotUnlock?: {
      ui: () => SlotUnlockUi;
      where: (label: string) => { x: number; y: number } | null;
      press: (label: string) => boolean;
    };
  }
}

async function load(url: string | null | undefined): Promise<Texture | null> {
  if (!url) return null;
  try {
    return await Assets.load<Texture>(url);
  } catch {
    return null;
  }
}

/**
 * Mount the slot unlock board into `el` on its own transparent Pixi canvas,
 * so it shows the same over the slot and over the map.
 */
export function mountSlotUnlockBoard(el: HTMLElement, options: MountSlotUnlockOptions): SlotUnlockHandle {
  const app = new Application();
  let gone = false;
  let board: SlotUnlockBoard | null = null;
  let pending: SlotUnlockView = options.view;
  const fit = (): void => board?.layout(app.screen.width, app.screen.height);
  // Keys stop here: the slot underneath listens on the window, and a space
  // or an "i" mustn't spin it or open its paytable behind the board.
  const onKey = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    if (event.key === 'Escape' && board && board.mode !== 'downloading' && board.mode !== 'ready') options.onPress('cancel');
  };
  window.addEventListener('keydown', onKey, true);
  const ready = (async () => {
    await app.init({
      resizeTo: el,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });
    if (gone) return;
    el.appendChild(app.canvas);
    const { art } = options;
    const [floor, gem, lock, check, track, fill] = await Promise.all([
      load(art.floor),
      load(art.gem),
      load(art.lock),
      load(art.check),
      load(art.track),
      load(art.fill),
      loadUiFonts(),
      options.plates ? AssetLoader.shared.loadAssets(options.plates).catch(() => undefined) : undefined,
    ]);
    if (gone) return;
    board = new SlotUnlockBoard({ floor, gem, lock, check, track, fill }, options.onPress);
    board.show(pending);
    app.stage.addChild(board);
    fit();
    app.renderer.on('resize', fit);
    window.__slotUnlock = {
      ui: () => board!.ui(),
      where: (label) => board!.where(label),
      press: (label) => board!.press(label),
    };
    // The picture comes from the CDN: the board opens without waiting.
    void load(options.pictureUrl).then((texture) => {
      if (!gone) board?.setPicture(texture);
    });
    await board.open();
  })();
  return {
    show: (view) => {
      pending = view;
      board?.show(view);
    },
    destroy: () => {
      gone = true;
      window.removeEventListener('keydown', onKey, true);
      delete window.__slotUnlock;
      // Only this canvas goes (the slot's renderer shares Pixi's global
      // resources), and the floor, icons and plates stay loaded: they're the
      // game's too.
      void ready.finally(() => destroyPixiApp(app, 'slot unlock'));
    },
  };
}
